import { CircularTimer } from "./CircularTimer"
import { MessageQueue } from "./MessageQueue"
import "./info-panel.css"
import "./circular-timer.css"
import type { Message } from "./Message"

const canAnimate = true
export class InfoPanelClass {
    Type = {
        Info: "info",
        Warning: "warning",
        Error: "danger",
        Success: "success",
        Star: "star",
    }

    base: typeof $
    container: typeof $
    circleTimer: CircularTimer
    title = null
    content: typeof $
    progressBar: typeof $
    hideMessageTimeout: number | null = null
    isWindowFocused = true
    maxDisplayMessagesCount = 1
    queue = {
        instance: new MessageQueue(),
        running: false,
    }
    timer = {
        id: -1,
        defaultRunTime: 3000,
        currentRunTime: 0,
        displayUntil: 0,
    }

    constructor() {
        this.base = $("#info-panel")
        this.container = this.base.find(".panel")
        this.content = this.container.find(".panel-body")
        this.progressBar = this.container.find(".timer-bar")

        this.circleTimer = new CircularTimer("info-panel-timer")

        if ("hasFocus" in document && !document.hasFocus()) {
            this.isWindowFocused = false
        }

        this.base.on("click", ".panel-close", this.hideMessage.bind(this))
        this.base.on("mouseenter", this.pauseMessageQueue.bind(this))
        this.base.on("mouseleave", this.runMessageQueue.bind(this))

        $(window).focus(this.windowGainsFocus.bind(this))
        $(window).blur(this.windowLosesFocus.bind(this))

        window.setTimeout(this.checkForSessionMessage.bind(this), 100)
    }

    displayMessage(message: string, type = "", duration = 3000, title = "") {
        const messageType = type || this.Type.Info
        const messageTitle = title || ""
        const messageDuration = duration || this.timer.defaultRunTime

        const existingMessageKey = this.queue.instance.findItem(messageType, messageTitle, messageDuration)

        if (false !== existingMessageKey) {
            this.queue.instance.alterMessage(existingMessageKey, message)

            if (0 === existingMessageKey && this.queue.running) {
                this.restartMessageQueue()
            }
        } else {
            this.queue.instance.addMessage(messageType, messageTitle, message, messageDuration)
        }

        if (!this.queue.running) {
            this.runMessageQueue()
        }
    }

    runMessageQueue() {
        if (this.queue.instance.isEmpty()) {
            return
        }

        this.queue.running = true

        const currentMessage = this.queue.instance.peekNextMessage()
        if (!currentMessage) return
        this.timer.currentRunTime = currentMessage.duration
        this.circleTimer.animationDuration((currentMessage.duration / 1000).toString())

        this.container.queue("messageQueue", [
            this.showMessage.bind(this, currentMessage),
            this.runQueueTimer.bind(this),
            this.runMessageQueue.bind(this),
        ])

        this.container.dequeue("messageQueue")
    }

    pauseMessageQueue() {
        if (canAnimate) {
            this.circleTimer.stopTimer()
        }

        this.content.find(".message-line").each(() => {
            if (undefined !== $(this).data("timerId")) {
                $(this).removeClass("fading")
                window.clearTimeout($(this).data("timerId"))
            }
        })

        if (this.timer.id >= 0) {
            this.clearProgressTimer()
        }

        this.queue.running = false
    }

    restartMessageQueue() {
        this.container.clearQueue("messageQueue")

        if (canAnimate) {
            this.circleTimer.stopTimer()
            window.setTimeout(this.runMessageQueue.bind(this), 100)

            return
        }

        if (this.timer.id >= 0) {
            this.clearProgressTimer()
        }

        this.queue.running = false
    }

    runQueueTimer() {
        if (this.isWindowFocused) {
            if (!canAnimate) {
                this.timer.displayUntil = new Date().getTime() + this.timer.currentRunTime
                this.timer.id = window.setInterval(this.updateProgress.bind(this), 10)

                $("#info-panel-timer").addClass("no-animation")
                return
            }

            if (this.content.find(".message-line").length > 1) {
                this.circleTimer.stopTimer()
                this.fadeOutMessageLine()

                return
            }

            this.circleTimer.startTimer(() => {
                this.clearProgressTimer()
                this.hideMessage()
            })

            return
        }

        window.setTimeout(this.runQueueTimer.bind(this), 50)
    }

    updateProgress() {
        const currentTime = new Date().getTime()
        const timeDifference = this.timer.displayUntil - currentTime
        let progress = 100 - (timeDifference / this.timer.currentRunTime) * 100

        if (this.content.find(".message-line").length > 1) {
            this.fadeOutMessageLine()
            return
        }

        if (progress > 100) {
            progress = 100
        }

        this.changeProgressDisplay(progress)

        if (100 === progress) {
            this.clearProgressTimer()
            this.hideMessage()
        }
    }

    fadeOutMessageLine() {
        let lineIndex = 0
        const fadeTime = this.timer.currentRunTime / 2

        if (!this.progressBar.data("fading")) {
            this.progressBar.data("fading", true)

            this.resetProgressDisplay()
        }

        const multiLines = this.content.find(".message-line").slice(1).not(".fading")
        const lineCount = multiLines.length

        for (; lineIndex < lineCount; ++lineIndex) {
            const fadeDelay = (lineCount - lineIndex - 1) * (fadeTime - this.timer.currentRunTime / 10)

            const currentLine = $(multiLines[lineIndex])
            currentLine.addClass("fading")

            currentLine.data("timerId", window.setTimeout(this.hideLine.bind(this, currentLine), fadeTime + fadeDelay))
        }
    }

    changeProgressDisplay(progress: string | number) {
        this.progressBar.css({
            width: `${progress}%`,
            opacity: 1,
        })
    }

    resetProgressDisplay() {
        this.progressBar.animate({ opacity: 0 }, 250, () => {
            this.changeProgressDisplay.bind(this, 0)
            this.progressBar.data("fading", false)
        })
    }

    clearProgressTimer() {
        window.clearInterval(this.timer.id)
        this.timer.id = -1
    }

    showMessage(messageData: Message) {
        if (null !== this.hideMessageTimeout) {
            window.clearTimeout(this.hideMessageTimeout)
            this.hideMessageTimeout = null
        }

        this.container.removeClass(this.removeIconClasses).addClass(`icon-${messageData.type}`)

        this.content.prepend(messageData.message())

        if (this.content.find(".message-line").length > this.maxDisplayMessagesCount) {
            this.content.find(".message-line").slice(this.maxDisplayMessagesCount).remove()
        }

        this.base.addClass("in inner")
        this.container.dequeue("messageQueue")
    }

    checkForSessionMessage() {
        const message = window.sessionStorage.getItem("displayInfoPanel")

        if ("string" !== typeof message) {
            return
        }

        const messageData = JSON.parse(message)
        window.sessionStorage.removeItem("displayInfoPanel")

        if (!messageData.type) {
            messageData.type = this.Type.Info
        }

        if (!messageData.duration) {
            messageData.duration = this.timer.defaultRunTime
        }

        if (!messageData.title) {
            messageData.title = null
        }

        this.displayMessage(messageData.message, messageData.type, messageData.duration, messageData.title)
    }

    hideMessage() {
        const currentMessage = this.queue.instance.peekNextMessage()

        if (currentMessage && "" !== currentMessage.title) {
            $(document).trigger("hide.InfoPanel:Message", [currentMessage.title])
        }

        this.queue.instance.popMessage()
        this.base.removeClass("inner")

        if (this.queue.instance.isEmpty()) {
            this.queue.running = false
            this.base.removeClass("in")
        }

        this.hideMessageTimeout = window.setTimeout(this.nextQueueItem.bind(this), 500)
    }

    hideLine(line: typeof $) {
        line.stop().animate({ opacity: 0 }, 250, () => {
            this.timer.displayUntil = new Date().getTime() + this.timer.currentRunTime
            line.remove()

            if (this.timer.id < 0 && this.queue.running) {
                window.setTimeout(this.runQueueTimer.bind(this), 1)
            }
        })
    }

    nextQueueItem() {
        this.changeProgressDisplay(0)
        this.content.empty()

        this.container.dequeue("messageQueue")
    }

    windowGainsFocus() {
        this.isWindowFocused = true
        this.runMessageQueue()
    }

    windowLosesFocus() {
        this.pauseMessageQueue()
        this.isWindowFocused = false
    }

    removeIconClasses(_: number, classes: string) {
        return (classes.match(/icon-\S+/g) || []).join(" ")
    }
}
export const InfoPanel = new InfoPanelClass()
